import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { AudioMessage } from "@models/audio-message";
import { CompanionResponse } from "@models/companion-response";
import prepareAuthHeaders from "@utils/prepareAuthHeaders";
import { debugCompanionPacing } from "@utils/companion-pacing-debug";
import { ListResponse } from "@models/list-response";
import { Companion } from "@models/companion";

const BASE_API_URL = process.env.REACT_APP_API_URL;

export const companionApi = createApi({
  reducerPath: "companionApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${BASE_API_URL}/companion`,
    prepareHeaders: prepareAuthHeaders,
  }),
  tagTypes: ["Companion"],
  endpoints: (builder) => ({
    sendAudioMessage: builder.mutation<CompanionResponse, AudioMessage>({
      query: ({
        sessionId,
        photoId,
        audio,
        eventType,
        conversationPacing,
      }: AudioMessage) => {
        const formData = new FormData();
        formData.append("sessionId", sessionId);
        if (audio) {
          formData.append("audio", audio);
        }
        if (photoId) {
          formData.append("photoId", photoId);
        }
        if (eventType) {
          formData.append("eventType", eventType);
        }
        formData.append(
          "conversationPacing",
          conversationPacing ?? "balanced",
        );
        debugCompanionPacing("REST sendAudioMessage", {
          sessionId,
          photoId,
          eventType,
          conversationPacing: conversationPacing ?? "balanced",
          audioBytes: audio ? audio.size : 0,
        });
        return {
          url: "message",
          method: "POST",
          body: formData,
          formData: true,
        };
      },
    }),
    getCompanions: builder.query<
      ListResponse<Companion>,
      { userId?: string, patientId?: string }
    >({
      providesTags: ["Companion"],
      query: ({ patientId, userId }) => {
        return {
          url: "",
          method: "GET",
          params: {
            userId,
            patientId,
          },
        };
      },
    }),
    createCompanion: builder.mutation<Companion, Partial<Companion>>({
      invalidatesTags: ["Companion"],
      query: (body) => {
        return {
          url: "",
          method: "POST",
          body,
        };
      },
    }),
    updateCompanion: builder.mutation<
      Companion,
      Partial<Companion> & { id: string }
    >({
      invalidatesTags: ["Companion"],
      query: ({ id, ...body }) => {
        return {
          url: id!,
          method: "PATCH",
          body,
        };
      },
    }),
  }),
});

export const { useSendAudioMessageMutation,
  useGetCompanionsQuery,
  useCreateCompanionMutation,
  useUpdateCompanionMutation
} = companionApi;
